import * as configurationsActions from '@Actions/configurations';
import { navigateTo } from '@Actions/navigate.action';
import { error as notificationError, success } from '@innovyze/stylovyze';
import { PayloadAction } from '@reduxjs/toolkit';
import { getAssetsByIdStartsWith } from '@Services/assets';
import {
	createConfigurations as createConfigurationsService,
	deleteConfigurationById as deleteConfigurationService,
	deleteMultipleConfigurations,
	getConfigurationById,
	getConfigurationsRfi,
	getConfigurations as getConfigurationsService,
	getSensorsReadingsCount,
	updateConfigurationById as updateConfigurationsService,
} from '@Services/configurations';
import { Configuration } from '@Types/configuration.types';
import {
	ConfigurationParams,
	ConfigurationParamsRfi,
	ConfigurationParamsWithSensorId,
	ConfigurationsErrors,
	CreateConfigurationParams,
	DeleteConfigurationParams,
	MultipleDeleteParams,
	PaginatedConfigurations,
	SensorConfigFormShape,
	UpdateConfigurationResponse,
} from '@Types/configurations.types';
import { generateFileFrom, isVirtualSensor } from '@Utils';
import { addRouteNamespace } from '@Utils/actions';
import { AxiosError, AxiosResponse } from 'axios';
import { call, put, retry, takeLatest } from 'redux-saga/effects';

function* getConfigurationDetailsByIdSaga(
	action: PayloadAction<Configuration>,
) {
	const { getConfigurationDetailsResolved, getConfigurationDetailsRejected } =
		configurationsActions;

	const config = action.payload;

	try {
		if (!config) {
			throw new Error('OperationStatus != SUCCESS');
		}
		const configDetails = { ...config };
		const { assetId, sensorType } = config;
		if (sensorType === 'Tank Level' && assetId) {
			const assetResponse: AxiosResponse<
				{
					assetId: string;
					assetType: string;
					tankCurveId?: string;
				}[]
			> = yield retry(5, 1500, getAssetsByIdStartsWith, assetId);
			if (assetResponse.data.length > 0) {
				const [{ assetType, tankCurveId }] = assetResponse.data;
				if (assetType === 'tank' && tankCurveId) {
					configDetails.tankCurveId = tankCurveId;
				}
			}
		}
		yield put(getConfigurationDetailsResolved(configDetails));
	} catch (e) {
		yield put(getConfigurationDetailsRejected());
	}
}

function* getConfigurationByIdSaga(
	action: PayloadAction<ConfigurationParamsWithSensorId>,
) {
	const { getConfigurationByIdResolved, getConfigurationByIdRejected } =
		configurationsActions;

	const { sensorId, ...params } = action.payload;

	try {
		const response: AxiosResponse<Configuration> = yield retry(
			5,
			1500,
			getConfigurationById,
			sensorId,
			params,
		);

		if (response.data) {
			const { assetId, sensorType } = response.data;
			if (sensorType === 'Tank Level' && assetId) {
				const assetResponse: AxiosResponse<
					// eslint-disable-next-line prettier/prettier
					{
						assetId: string;
						assetType: string;
						tankCurveId?: string;
					}[]
				> = yield retry(5, 1500, getAssetsByIdStartsWith, assetId);
				if (assetResponse.data.length > 0) {
					const [{ assetType, tankCurveId }] = assetResponse.data;
					if (assetType === 'tank' && tankCurveId) {
						response.data.tankCurveId = tankCurveId;
					}
				}
			}

			// @ts-ignore
			if (!response.data.configuration) {
				yield put(
					notificationError('Error Fetching Sensor Configuration'),
				);
			}

			yield put(getConfigurationByIdResolved(response.data));
		} else {
			yield put(notificationError('Error Fetching Sensor Configuration'));
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(notificationError('Error Fetching Sensor Configuration'));
		yield put(getConfigurationByIdRejected());
	}
}

function* getConfigurationsSaga(action: PayloadAction<ConfigurationParams>) {
	const { getConfigurationsResolved, getConfigurationsRejected } =
		configurationsActions;
	try {
		const payload: AxiosResponse<PaginatedConfigurations> = yield retry(
			5,
			1500,
			getConfigurationsService,
			action.payload,
		);
		if (payload.data) {
			payload.data.configurations = payload.data.configurations.filter(
				({ sensorId }) => !!sensorId,
			);
			yield put(getConfigurationsResolved(payload.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(getConfigurationsRejected());
	}
}

const buildCreateConfigurationParams = (
	payload: SensorConfigFormShape,
): CreateConfigurationParams => {
	if (isVirtualSensor(payload.dataSourceType)) {
		return {
			dataSourceType: payload.dataSourceType,
			location: payload.location,
			assetId: payload.assetId,
		};
	}
	if (payload.isPhysical === 'false') {
		return {
			sensorId: payload.sensorId,
			sensorType: payload.sensorType,
			unit: payload.unit,
			collectionInterval: payload.collectionInterval || '300',
			assetId: payload.assetId,
			sensorAlias: payload.sensorAlias,
			location: payload.location,
			configType: 'sensor',
			tankCurveId:
				payload.sensorType === 'Tank Level' &&
				payload.tankType === 'non-cylindrical'
					? payload.tankCurveId
					: '',
			tankType: payload.tankType,
			tankOffset:
				payload.sensorType === 'Tank Level' &&
				payload.tankType === 'cylindrical'
					? +payload.tankOffset
					: null,
			tankDiameter:
				payload.sensorType === 'Tank Level' &&
				payload.tankType === 'cylindrical'
					? +payload.tankDiameter
					: null,
			dataSourceType: payload.dataSourceType,
			isPhysical: false,
		};
	} else {
		return {
			...payload,
			sensorId: payload.sensorId,
			connectionString: undefined,
			sensorType: payload.sensorType,
			unit: payload.unit,
			collectionInterval: payload.collectionInterval,
			assetId: payload.assetId,
			sensorAlias: payload.sensorAlias,
			location: payload.location,
			sensorTable: payload.sensorTable,
			sensorDateField: payload.sensorDateField,
			sensorTimeField: payload.sensorTimeField,
			sensorChannelField: payload.sensorChannelField,
			sensorUnitConversion: payload.sensorUnitConversion,
			configType: 'sensor',
			enableTelemetry: false,
			sensorIdField: payload.sensorIdField,
			interpolationEnabled: payload.interpolationEnabled,
			interpolationMethod: payload.interpolationMethod,
			tankCurveId:
				payload.sensorType === 'Tank Level' &&
				payload.tankType === 'non-cylindrical'
					? payload.tankCurveId
					: '',
			tankType: payload.tankType,
			tankOffset:
				payload.sensorType === 'Tank Level' &&
				payload.tankType === 'cylindrical'
					? +payload.tankOffset
					: null,
			tankDiameter:
				payload.sensorType === 'Tank Level' &&
				payload.tankType === 'cylindrical'
					? +payload.tankDiameter
					: null,
			dataSourceId: payload.dataSourceId,
			tagValue: payload.tagValue,
			dataSourceType: payload.dataSourceType,
			dataSourceName: payload.dataSourceName,
			isPhysical: true,
		};
	}
};

function* handleCreateSuccess(
	payload: AxiosResponse<CreateConfigurationParams>,
) {
	const { createConfigurationResolved } = configurationsActions;

	if (payload.data) {
		yield put(success('Sensor Configuration Successfully Created!'));
		yield put(createConfigurationResolved(payload.data));
	} else {
		yield put(notificationError('Error Creating Sensor Configuration'));
		throw new Error('OperationStatus != SUCCESS');
	}
}

function* createConfigurationSaga(
	action: PayloadAction<SensorConfigFormShape>,
) {
	const { createConfigurationRejected } = configurationsActions;
	try {
		const payload: AxiosResponse<CreateConfigurationParams> = yield call(
			createConfigurationsService,
			buildCreateConfigurationParams(action.payload),
		);

		yield call(handleCreateSuccess, payload);
	} catch (e) {
		const err = e as AxiosError<{
			error: { errorCategory: string; message: string } | undefined;
		}>;
		if (
			err.response?.data?.error?.errorCategory ===
			ConfigurationsErrors.DuplicationError
		) {
			yield put(
				createConfigurationRejected({
					errorCategory: err.response?.data?.error?.errorCategory,
					errorMsg: err.response?.data?.error?.message,
				}),
			);
		} else {
			try {
				const payload: AxiosResponse<CreateConfigurationParams> =
					yield retry(
						4,
						1500,
						createConfigurationsService,
						buildCreateConfigurationParams(action.payload),
					);

				yield call(handleCreateSuccess, payload);
			} catch (err) {
				yield put(
					notificationError(
						'Error Creating Sensor Configuration: ' + err,
					),
				);
				yield put(createConfigurationRejected({}));
			}
		}
	}
}

function* updateConfigurationSaga(
	action: PayloadAction<{
		configurationId: string;
		payload: SensorConfigFormShape;
	}>,
) {
	const {
		updateConfigurationResolved,
		updateConfigurationRejected,
		mapConfigurationToSensor,
	} = configurationsActions;
	const { configurationId, payload } = action.payload;
	if (!configurationId) {
		yield put(notificationError('Error Updating Sensor Configuration'));
		throw new Error('OperationStatus != SUCCESS');
	}
	try {
		const response: AxiosResponse<UpdateConfigurationResponse> =
			yield retry(
				5,
				1500,
				updateConfigurationsService,
				configurationId,
				buildCreateConfigurationParams(payload),
			);
		if (response.data) {
			const value = response.data.configuration;
			yield put(success('Sensor Configuration Successfully Updated!'));
			yield put(updateConfigurationResolved(value));
			yield put(mapConfigurationToSensor(value));
		} else {
			yield put(notificationError('Error Updating Sensor Configuration'));
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(notificationError('Error Updating Sensor Configuration'));
		yield put(updateConfigurationRejected());
	}
}

function* deleteConfigurationSaga(
	action: PayloadAction<Pick<DeleteConfigurationParams, 'sensorId'>>,
) {
	const { deleteConfigurationResolved, deleteConfigurationRejected } =
		configurationsActions;
	const { sensorId } = action.payload;
	try {
		const payload: AxiosResponse<unknown> = yield retry(
			3,
			1500,
			deleteConfigurationService,
			sensorId,
		);

		if (payload.status === 200) {
			yield put(success('Sensor Configuration Successfully Deleted!'));
			yield put(deleteConfigurationResolved({ sensorId: sensorId }));
			yield put(navigateTo(addRouteNamespace('sensor-configuration')));
		} else {
			yield put(notificationError('Error Deleting Sensor Configuration'));
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(notificationError('Error Deleting Sensor Configuration'));
		yield put(deleteConfigurationRejected());
	}
}

function* getConfigurationsRfiSaga(
	action: PayloadAction<ConfigurationParamsRfi>,
) {
	try {
		const responses = [];
		const { fileName, requestParams } = action.payload;
		while (true) {
			const payload: AxiosResponse<string> = yield retry(
				3,
				1500,
				getConfigurationsRfi,
				requestParams,
			);
			if (!payload.data || !payload.data.length) break;
			responses.push(payload.data);
			requestParams.offset++; // ? next page
		}
		if (!responses.length) throw new Error('OperationStatus != SUCCESS');
		// ? download
		generateFileFrom(fileName, responses);
		yield put(success('RFI file downloaded'));
	} catch (e) {
		yield put(notificationError('Error downloading RFI file'));
	}
}

function* getSensorsReadingsCountSaga(
	action: PayloadAction<Partial<ConfigurationParamsRfi>>,
) {
	try {
		const { fileName } = action.payload;
		if (!fileName) {
			throw new Error('fileName not provided');
		}
		const { data }: AxiosResponse = yield retry(
			5,
			1500,
			getSensorsReadingsCount,
		);
		generateFileFrom(fileName, data);
		yield put(success('Sensors count file downloaded'));
	} catch (e) {
		yield put(notificationError('Error downloading Sensors count file'));
	}
}

function* deleteMultipleConfigurationsSaga(
	action: PayloadAction<MultipleDeleteParams>,
) {
	try {
		const { payload } = action;
		yield retry(5, 1500, deleteMultipleConfigurations, payload);
		yield put(
			configurationsActions.deleteMultipleConfigurationsResolved(payload),
		);
		if (payload.onlyData) {
			yield put(success('Sensor data deletion in progress'));
		} else {
			yield put(success('Sensor deletion in progress'));
		}
	} catch (e) {
		yield put(configurationsActions.deleteMultipleConfigurationsRejected());
		if (action.payload.onlyData) {
			yield put(notificationError('Error deleting sensor(s) data'));
		} else {
			yield put(notificationError('Error deleting sensor(s)'));
		}
	}
}

function* watchDeleteMultipleConfigurations() {
	yield takeLatest(
		configurationsActions.deleteMultipleConfigurations,
		deleteMultipleConfigurationsSaga,
	);
}

function* watchGetConfigurationById() {
	yield takeLatest(
		configurationsActions.getConfigurationById,
		getConfigurationByIdSaga,
	);
}

function* watchGetConfigurationDetailsById() {
	yield takeLatest(
		configurationsActions.getConfigurationDetails,
		getConfigurationDetailsByIdSaga,
	);
}

function* watchGetConfigurations() {
	yield takeLatest(
		configurationsActions.getConfigurations,
		getConfigurationsSaga,
	);
}

function* watchCreateConfiguration() {
	yield takeLatest(
		configurationsActions.createConfiguration,
		createConfigurationSaga,
	);
}

function* watchUpdateConfiguration() {
	yield takeLatest(
		configurationsActions.updateConfiguration,
		updateConfigurationSaga,
	);
}

function* watchDeleteConfiguration() {
	yield takeLatest(
		configurationsActions.deleteConfiguration,
		deleteConfigurationSaga,
	);
}

function* watchGetConfigurationsRfi() {
	yield takeLatest(
		configurationsActions.getSensorsRfiFile,
		getConfigurationsRfiSaga,
	);
}

function* watchGetSensorsReadingsCount() {
	yield takeLatest(
		configurationsActions.getSensorsReadingsCount,
		getSensorsReadingsCountSaga,
	);
}

export default [
	watchGetConfigurations(),
	watchGetConfigurationDetailsById(),
	watchCreateConfiguration(),
	watchGetConfigurationById(),
	watchUpdateConfiguration(),
	watchDeleteConfiguration(),
	watchGetConfigurationsRfi(),
	watchGetSensorsReadingsCount(),
	watchDeleteMultipleConfigurations(),
];
